import { useEffect, useState } from 'react';
import {
  Button, TextField, Grid, Typography, Modal, Box, Alert
} from '@mui/material';
import PropTypes from 'prop-types';

import { changePass } from '../../actions/users';
import { isEmpty } from '../common/helpers';
import { errorDeContrasena, REGLA_CONTRASENA } from '../../utils/contrasena';

const VACIO = { passwordActual: '', password: '', password2: '' };
const SIN_ERRORES = { passwordActual: '', password: '', password2: '', general: '' };

// Cambiar la contraseña (pide la ACTUAL) o, en una cuenta que entra con Google o Facebook y aún no tiene, crear la primera.
// Al guardarla el servidor cierra las sesiones de los otros dispositivos; esta sigue con un token nuevo que backendAxios guarda solo.
const CrearPassModal = ({
  show, handleClose, setModalStatus, setModalPass, tienePassword = true, onGuardada, onVolverAEntrar
}) => {
  const [data, setData] = useState(VACIO);
  const [errores, setErrores] = useState(SIN_ERRORES);
  const [guardando, setGuardando] = useState(false);
  // El servidor pide volver a entrar antes de crear la primera contraseña (la sesión es de hace mucho)
  const [requiereLogin, setRequiereLogin] = useState(false);

  useEffect(() => {
    if (show) { setData(VACIO); setErrores(SIN_ERRORES); setRequiereLogin(false); }
  }, [show]);

  const cambiarPass = e => {
    setData({
      ...data,
      [e.target.name]: e.target.value
    });
    if (errores[e.target.name] || errores.general) setErrores({ ...errores, [e.target.name]: '', general: '' });
  }

  const guardar = async e => {
    e.preventDefault();
    if (tienePassword && isEmpty(data.passwordActual)) {
      return setErrores({ ...SIN_ERRORES, passwordActual: 'Escribe tu contraseña actual' });
    }
    if(isEmpty(data.password)){
      return setErrores({
        ...SIN_ERRORES,
        password: 'Un campo esta vacio',
        password2: 'Un campo esta vacio'
      })
    }
    if(data.password !== data.password2){
      return setErrores({
        ...SIN_ERRORES,
        password: 'No coinciden',
        password2: 'No coinciden'
      });
    }
    const errorPass = errorDeContrasena(data.password);
    if(errorPass){
      return setErrores({
        ...SIN_ERRORES,
        password: errorPass,
        password2: errorPass
      });
    }
    const info = tienePassword
      ? { passwordActual: data.passwordActual, password: data.password }
      : { password: data.password };
    setGuardando(true);
    const resChange = await changePass(info);
    setGuardando(false);
    if(resChange.ok){
      setModalPass(false);
      setModalStatus({
        show: true,
        type: "SUCCESS",
        message: tienePassword
          ? "La contraseña fue cambiada satisfactoriamente. Cerramos tu sesión en tus otros dispositivos."
          : "Tu contraseña quedó creada. Ya puedes entrar también con tu correo y esta contraseña."
      });
      if (onGuardada) onGuardada();
      return;
    }
    // El motivo del servidor se muestra aquí mismo para poder corregirlo sin perder lo escrito
    if (resChange.campo === 'passwordActual') return setErrores({ ...SIN_ERRORES, passwordActual: resChange.msg });
    if (resChange.campo === 'password') return setErrores({ ...SIN_ERRORES, password: resChange.msg, password2: resChange.msg });
    setRequiereLogin(resChange.codigo === 'REQUIERE_LOGIN_RECIENTE');
    setErrores({ ...SIN_ERRORES, general: resChange.msg });
  }

  return (
    <Modal
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      open={show}
      aria-labelledby="server-modal-title"
      aria-describedby="server-modal-description"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "70%",
          maxWidth: "sm",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 5,
        }}
      >
        <Typography id="server-modal-title" color="primary" mb={3} mt={3} variant="h4" component="h5">
          {tienePassword ? 'Cambiar contraseña' : 'Crear contraseña'}
        </Typography>
        {!tienePassword && (
          <Typography id="server-modal-description" color="text.secondary" variant="body2" mb={1}>
            Tu cuenta entra con Google o Facebook. Crea una contraseña para entrar también con tu correo.
          </Typography>
        )}
        <form noValidate onSubmit={guardar}>
          <Grid container sx={{my:2}} spacing={{ sx: 1}}>
            {tienePassword && (
              <Grid size={{xs: 12}} sx={{ mb: 2 }}>
                <TextField
                  error={!isEmpty(errores.passwordActual)}
                  type="password"
                  variant="outlined"
                  fullWidth
                  id="passwordActual"
                  label="Contraseña actual"
                  name="passwordActual"
                  autoComplete="current-password"
                  value={data.passwordActual}
                  onChange={cambiarPass}
                  helperText={!isEmpty(errores.passwordActual) && errores.passwordActual}
                />
              </Grid>
            )}
            <Grid size={{xs: 12}}>
              <TextField
                error={!isEmpty(errores.password)}
                type="password"
                variant="outlined"
                fullWidth
                id="password"
                label={tienePassword ? 'Contraseña nueva' : 'Contraseña'}
                name="password"
                autoComplete="new-password"
                value={data.password}
                onChange={cambiarPass}
                helperText={!isEmpty(errores.password) && errores.password}
              />
            </Grid>
            <Grid className="content-description" size={{xs: 12}} sx={{ mb: 2 }}>
              <Typography color="text.secondary" variant="body2" sx={{marginTop: 2}}>
                {REGLA_CONTRASENA}
              </Typography>
            </Grid>
            <Grid size={{xs: 12}}>
              <TextField
                error={!isEmpty(errores.password2)}
                type="password"
                variant="outlined"
                fullWidth
                id="password2"
                label="Confirmar password"
                name="password2"
                autoComplete="new-password"
                value={data.password2}
                onChange={cambiarPass}
                helperText={!isEmpty(errores.password2) && errores.password2}
              />
            </Grid>
          </Grid>
          {!isEmpty(errores.general) && (
            <Alert
              severity={requiereLogin ? "warning" : "error"}
              sx={{ mb: 2, borderRadius: '12px' }}
              action={requiereLogin && onVolverAEntrar && (
                <Button color="inherit" size="small" onClick={onVolverAEntrar} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Volver a iniciar sesión
                </Button>
              )}
            >
              {errores.general}
            </Alert>
          )}
          <Grid size={12} sx={{mt: 3}}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={guardando}
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              fullWidth
              sx={{mt: 2}}
            >
              Cancelar
            </Button>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
}

CrearPassModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setModalStatus: PropTypes.func.isRequired,
  setModalPass: PropTypes.func.isRequired,
  tienePassword: PropTypes.bool,
  onGuardada: PropTypes.func,
  onVolverAEntrar: PropTypes.func,
}

export default CrearPassModal;
